/* eslint-disable @typescript-eslint/no-explicit-any */

import ks from "../ReUsableTS/kalmstromShared4";

/**
 * Builds and downloads a Power Platform solution ZIP containing a Power Automate cloud flow.
 * The generated ZIP can be imported via make.powerapps.com → Solutions → Import solution.
 *
 * Required Dataverse fields for Category=5 (ModernFlow):
 *   - PrimaryEntity: "none"  (must be present — its absence causes 0x80040216 NullReferenceException)
 *   - clientdata: JSON-escaped flow wrapper
 * Ref: https://learn.microsoft.com/en-us/power-automate/manage-flows-with-code
 */
export class kPowerAutomatePackage {

    // #region Public API

    /**
     * Assembles a Power Platform solution ZIP that contains the given Logic Apps flow definition.
     * @param flowDefinition   Logic Apps definition object ({ $schema, triggers, actions, ... }).
     * @param flowName         Display name of the flow, e.g. "TC - Create SharePoint Lists".
     * @param solutionBaseName Base name for the solution unique name, e.g. "TCCreateSharePointLists".
     * @param connectionApiNames  Connector API names and display names the flow uses.
     *                            e.g. [{ apiName: "shared_sharepointonline", displayName: "SharePoint Online" }]
     *                            Omit for flows that need no connections.
     * @returns Uint8Array containing the ZIP bytes.
     */
    public static Build = (
        flowDefinition: object,
        flowName: string,
        solutionBaseName: string,
        connectionApiNames?: { apiName: string; displayName: string }[],
        version?: string
    ): Uint8Array => {
        if (ks.kErrorMode === true) { return new Uint8Array(0); }
        try {
            const flowGuid: string = kPowerAutomatePackage._GenerateGuid();
            const flowGuidNoDashes: string = flowGuid.replace(/-/g, "");
            const solutionUniqueName: string = kPowerAutomatePackage._MakeSolutionUniqueName(solutionBaseName, flowGuidNoDashes);
            const sanitisedName: string = flowName.replace(/[^a-zA-Z0-9]/g, "_").replace(/_+/g, "_").replace(/^_|_$/g, "");
            const workflowFilename: string = sanitisedName + "_" + flowGuidNoDashes + ".json";
            const normalizedVersion: string = kPowerAutomatePackage._NormalizeVersion(version);

            // Connection references are declared in customizations.xml only.
            // They do NOT need a RootComponent entry in solution.xml — adding one causes
            // "Cannot add a Root Component of type 10" (EntityRelationship) import failure.
            const connRefs: { apiName: string; displayName: string }[] =
                (connectionApiNames ?? []).map((c: { apiName: string; displayName: string }): { apiName: string; displayName: string } =>
                    ({ apiName: c.apiName, displayName: c.displayName }));

            const apiNamesOnly: string[] = connRefs.map((c: { apiName: string }): string => c.apiName);
            const wrapper: object = kPowerAutomatePackage._BuildFlowWrapper(flowDefinition, flowName, apiNamesOnly);
            const definitionJson: string = JSON.stringify(wrapper, null, 2);
            const clientData: string = kPowerAutomatePackage._EscapeXml(definitionJson);

            const files: { name: string; data: string }[] = [
                { name: "[Content_Types].xml", data: kPowerAutomatePackage._BuildContentTypesXml() },
                { name: "solution.xml",        data: kPowerAutomatePackage._BuildSolutionXml(flowGuid, solutionUniqueName, flowName, normalizedVersion) },
                { name: "customizations.xml",  data: kPowerAutomatePackage._BuildCustomizationsXml(flowGuid, workflowFilename, clientData, flowName, connRefs, normalizedVersion) },
                { name: "Workflows/" + workflowFilename, data: definitionJson }
            ];

            return kPowerAutomatePackage._CreateZip(files);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "kPowerAutomatePackage.Build");
            return new Uint8Array(0);
        }
    };

    /**
     * Triggers a browser file download of a ZIP byte array.
     * @param zipData  ZIP bytes, as returned by Build().
     * @param filename Suggested filename for the download, e.g. "TC-CreateLists-Flow.zip".
     */
    public static Download = (zipData: Uint8Array, filename: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const blob: Blob = new Blob([zipData.buffer as ArrayBuffer], { type: "application/zip" });
            const url: string = URL.createObjectURL(blob);
            const link: HTMLAnchorElement = document.createElement("a");
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "kPowerAutomatePackage.Download");
        }
    };

    // #endregion

    // #region Flow JSON wrapper

    /**
     * Wraps a Logic Apps definition in the required Power Platform clientdata envelope.
     * schemaVersion is placed at root level (NOT inside properties).
     */
    private static _BuildFlowWrapper = (
        flowDefinition: object,
        flowName: string,
        connectionApiNames?: string[]
    ): object => {
        const connectionReferences: Record<string, object> = {};
        if (connectionApiNames && connectionApiNames.length > 0) {
            connectionApiNames.forEach((apiName: string): void => {
                // Key is the connector API name (e.g. "shared_sharepointonline").
                // This is what host.connectionName in the action must also use.
                //
                // runtimeSource notes (validated 2026-02-22):
                //   Omitted (owner mode) — flow engine resolves the connection from the Dataverse
                //     connection reference record. Compatible with BOTH the import wizard AND the
                //     PA Management REST API trigger (no APIM tokens required). USE THIS.
                //   "embedded" — looks for OAuth credentials baked into the flow definition.
                //     Causes "Connector not found" in the flow editor after import. DO NOT USE.
                //   "invoker" — requires X-MS-APIM-Tokens header at trigger time, injected only
                //     by the browser session via the APIM gateway. The Management REST API proxy
                //     strips custom headers before forwarding, so triggering programmatically
                //     always fails with InvokerConnectionOverrideFailed. DO NOT USE.
                //
                // The Dataverse logical name goes inside connection.connectionReferenceLogicalName;
                // the flow editor uses it to look up the linked connection in Dataverse.
                const logicalName: string = kPowerAutomatePackage._MakeConnRefLogicalName(apiName);
                connectionReferences[apiName] = {
                    connection: {
                        connectionReferenceLogicalName: logicalName
                    },
                    api: { name: apiName }
                };
            });
        }
        return {
            properties: {
                connectionReferences: connectionReferences,
                definition: flowDefinition,
                displayName: flowName,
                description: ""
            },
            schemaVersion: "1.0.0.0"
        };
    };

    // #endregion

    // #region XML builders

    private static _BuildContentTypesXml = (): string => {
        return '<?xml version="1.0" encoding="utf-8"?>\r\n'
            + '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">\r\n'
            + '  <Default Extension="xml" ContentType="application/xml" />\r\n'
            + '  <Default Extension="json" ContentType="application/json" />\r\n'
            + '</Types>';
    };

    private static _BuildSolutionXml = (
        flowGuid: string,
        solutionUniqueName: string,
        flowName: string,
        version: string
    ): string => {
        const escapedFlowName: string = kPowerAutomatePackage._EscapeXmlAttr(flowName);
        return '<?xml version="1.0" encoding="utf-8"?>\r\n'
            + '<ImportExportXml version="9.1.0.0" SolutionPackageVersion="9.1" languagecode="1033"'
            + ' generatedBy="CrmExporter" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">\r\n'
            + '  <SolutionManifest>\r\n'
            + `    <UniqueName>${solutionUniqueName}</UniqueName>\r\n`
            + '    <LocalizedNames>\r\n'
            + `      <LocalizedName description="${escapedFlowName}" languagecode="1033" />\r\n`
            + '    </LocalizedNames>\r\n'
            + '    <Descriptions />\r\n'
            + `    <Version>${version}</Version>\r\n`
            + '    <Managed>0</Managed>\r\n'
            + '    <Publisher>\r\n'
            + '      <UniqueName>DefaultPublisherForDefault</UniqueName>\r\n'
            + '      <LocalizedNames>\r\n'
            + '        <LocalizedName description="Default Publisher for Default" languagecode="1033" />\r\n'
            + '      </LocalizedNames>\r\n'
            + '      <Descriptions />\r\n'
            + '      <CustomizationPrefix>new</CustomizationPrefix>\r\n'
            + '      <CustomizationOptionValuePrefix>10000</CustomizationOptionValuePrefix>\r\n'
            + '      <Addresses>\r\n'
            + '        <Address>\r\n'
            + '          <AddressNumber>1</AddressNumber>\r\n'
            + '          <AddressTypeCode>1</AddressTypeCode>\r\n'
            + '          <City xsi:nil="true" />\r\n'
            + '          <County xsi:nil="true" />\r\n'
            + '          <Country xsi:nil="true" />\r\n'
            + '          <Fax xsi:nil="true" />\r\n'
            + '          <FreightTermsCode xsi:nil="true" />\r\n'
            + '          <ImportSequenceNumber xsi:nil="true" />\r\n'
            + '          <Latitude xsi:nil="true" />\r\n'
            + '          <Longitude xsi:nil="true" />\r\n'
            + '          <Line1 xsi:nil="true" />\r\n'
            + '          <Line2 xsi:nil="true" />\r\n'
            + '          <Line3 xsi:nil="true" />\r\n'
            + '          <Name xsi:nil="true" />\r\n'
            + '          <PostalCode xsi:nil="true" />\r\n'
            + '          <PostOfficeBox xsi:nil="true" />\r\n'
            + '          <PrimaryContactName xsi:nil="true" />\r\n'
            + '          <ShippingMethodCode>1</ShippingMethodCode>\r\n'
            + '          <StateOrProvince xsi:nil="true" />\r\n'
            + '          <Telephone1 xsi:nil="true" />\r\n'
            + '          <Telephone2 xsi:nil="true" />\r\n'
            + '          <Telephone3 xsi:nil="true" />\r\n'
            + '          <UPSZone xsi:nil="true" />\r\n'
            + '          <UTCOffset xsi:nil="true" />\r\n'
            + '          <WebSiteURL xsi:nil="true" />\r\n'
            + '        </Address>\r\n'
            + '      </Addresses>\r\n'
            + '    </Publisher>\r\n'
            + '    <RootComponents>\r\n'
            + `      <RootComponent type="29" id="{${flowGuid}}" behavior="0" />\r\n`
            + '    </RootComponents>\r\n'
            + '    <MissingDependencies />\r\n'
            + '  </SolutionManifest>\r\n'
            + '</ImportExportXml>';
    };

    /**
     * Builds the customizations.xml Workflow element for a Category=5 (ModernFlow).
     * StateCode=0/StatusCode=1: flow imports as Off; user connects the account then turns it on.
     * PrimaryEntity="none" is required for all instant/automated/scheduled flows.
     */
    private static _BuildCustomizationsXml = (
        flowGuid: string,
        workflowFilename: string,
        clientData: string,
        flowName: string,
        connRefs?: { apiName: string; displayName: string }[],
        version?: string
    ): string => {
        const escapedFlowName: string = kPowerAutomatePackage._EscapeXmlAttr(flowName);
        // connectorid, connectionreferencedisplayname, statecode, statuscode MUST be child elements.
        // Only connectionreferencelogicalname is an XML attribute on <connectionreference>.
        // Ref: Microsoft PowerApps-Samples/SolutionStagingAndImport (official sample).
        const connRefsXml: string = (connRefs && connRefs.length > 0)
            ? '  <connectionreferences>\r\n'
                + connRefs.map(({ apiName, displayName }: { apiName: string; displayName: string }): string => {
                    const logicalName: string = kPowerAutomatePackage._MakeConnRefLogicalName(apiName);
                    const escapedDisplayName: string = kPowerAutomatePackage._EscapeXml(displayName);
                    return `    <connectionreference connectionreferencelogicalname="${logicalName}">\r\n`
                        + `      <connectionreferencedisplayname>${escapedDisplayName}</connectionreferencedisplayname>\r\n`
                        + `      <connectorid>/providers/Microsoft.PowerApps/apis/${apiName}</connectorid>\r\n`
                        + `      <iscustomizable>1</iscustomizable>\r\n`
                        + `      <promptingbehavior>0</promptingbehavior>\r\n`
                        + `      <statecode>0</statecode>\r\n`
                        + `      <statuscode>1</statuscode>\r\n`
                        + `    </connectionreference>\r\n`;
                }).join("")
                + '  </connectionreferences>\r\n'
            : '';
        return '<?xml version="1.0" encoding="utf-8"?>\r\n'
            + '<ImportExportXml version="9.1.0.0" SolutionPackageVersion="9.1" languagecode="1033"'
            + ' generatedBy="CrmExporter" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">\r\n'
            + connRefsXml
            + '  <Workflows>\r\n'
            + `    <Workflow WorkflowId="{${flowGuid}}" Name="${escapedFlowName}">\r\n`
            + `      <JsonFileName>/Workflows/${workflowFilename}</JsonFileName>\r\n`
            + `      <clientdata>${clientData}</clientdata>\r\n`
            + '      <Category>5</Category>\r\n'
            + '      <PrimaryEntity>none</PrimaryEntity>\r\n'
            + '      <Mode>0</Mode>\r\n'
            + '      <Scope>4</Scope>\r\n'
            + '      <OnDemand>1</OnDemand>\r\n'
            + '      <TriggerOnCreate>0</TriggerOnCreate>\r\n'
            + '      <TriggerOnDelete>0</TriggerOnDelete>\r\n'
            + '      <AsyncAutodelete>0</AsyncAutodelete>\r\n'
            + '      <SyncWorkflowLogOnFailure>0</SyncWorkflowLogOnFailure>\r\n'
            + '      <StateCode>0</StateCode>\r\n'
            + '      <StatusCode>1</StatusCode>\r\n'
            + '      <IsTransacted>1</IsTransacted>\r\n'
            + `      <IntroducedVersion>${version ?? "1.0.0.0"}</IntroducedVersion>\r\n`
            + '      <IsCustomizable>1</IsCustomizable>\r\n'
            + '      <IsCustomProcessingStepAllowedForOtherPublishers>1</IsCustomProcessingStepAllowedForOtherPublishers>\r\n'
            + '      <Description />\r\n'
            + '      <LocalizedNames>\r\n'
            + `        <LocalizedName languagecode="1033" description="${escapedFlowName}" />\r\n`
            + '      </LocalizedNames>\r\n'
            + '    </Workflow>\r\n'
            + '  </Workflows>\r\n'
            + '  <Languages>\r\n'
            + '    <Language>1033</Language>\r\n'
            + '  </Languages>\r\n'
            + '</ImportExportXml>';
    };

    // #endregion

    // #region ZIP generation

    /** Assembles a STORE-mode ZIP from an array of { name, data } entries. */
    private static _CreateZip = (files: { name: string; data: string }[]): Uint8Array => {
        const encoder: TextEncoder = new TextEncoder();
        const localHeaders: Uint8Array[] = [];
        const fileData: Uint8Array[] = [];
        const centralDirs: Uint8Array[] = [];
        const localOffsets: number[] = [];
        let offset: number = 0;

        // DOS date: 2024-01-01, time 00:00:00
        const dosDate: number = ((2024 - 1980) << 9) | (1 << 5) | 1;
        const dosTime: number = 0;

        for (const file of files) {
            const nameBytes: Uint8Array = encoder.encode(file.name);
            const dataBytes: Uint8Array = encoder.encode(file.data);
            const crc: number = kPowerAutomatePackage._Crc32(dataBytes);
            const size: number = dataBytes.length;

            localOffsets.push(offset);

            // Local file header (30 bytes + filename)
            const lh: Uint8Array = new Uint8Array(30 + nameBytes.length);
            const lv: DataView = new DataView(lh.buffer);
            lv.setUint32(0, 0x04034B50, true);      // PK\x03\x04 signature
            lv.setUint16(4, 20, true);               // version needed: 2.0
            lv.setUint16(6, 0, true);                // general purpose bit flag
            lv.setUint16(8, 0, true);                // compression: STORE
            lv.setUint16(10, dosTime, true);
            lv.setUint16(12, dosDate, true);
            lv.setUint32(14, crc, true);
            lv.setUint32(18, size, true);            // compressed size
            lv.setUint32(22, size, true);            // uncompressed size
            lv.setUint16(26, nameBytes.length, true);
            lv.setUint16(28, 0, true);               // extra field length
            lh.set(nameBytes, 30);
            localHeaders.push(lh);
            fileData.push(dataBytes);
            offset += lh.length + dataBytes.length;

            // Central directory entry (46 bytes + filename)
            const cd: Uint8Array = new Uint8Array(46 + nameBytes.length);
            const cv: DataView = new DataView(cd.buffer);
            cv.setUint32(0, 0x02014B50, true);       // PK\x01\x02 signature
            cv.setUint16(4, 20, true);               // version made by
            cv.setUint16(6, 20, true);               // version needed
            cv.setUint16(8, 0, true);
            cv.setUint16(10, 0, true);               // STORE
            cv.setUint16(12, dosTime, true);
            cv.setUint16(14, dosDate, true);
            cv.setUint32(16, crc, true);
            cv.setUint32(20, size, true);
            cv.setUint32(24, size, true);
            cv.setUint16(28, nameBytes.length, true);
            cv.setUint16(30, 0, true);               // extra field length
            cv.setUint16(32, 0, true);               // file comment length
            cv.setUint16(34, 0, true);               // disk number start
            cv.setUint16(36, 0, true);               // internal attributes
            cv.setUint32(38, 0, true);               // external attributes
            cv.setUint32(42, localOffsets[localOffsets.length - 1], true);
            cd.set(nameBytes, 46);
            centralDirs.push(cd);
        }

        let cdSize: number = 0;
        for (const cd of centralDirs) { cdSize += cd.length; }

        // End of central directory record (22 bytes)
        const eocd: Uint8Array = new Uint8Array(22);
        const ev: DataView = new DataView(eocd.buffer);
        ev.setUint32(0, 0x06054B50, true);           // PK\x05\x06 signature
        ev.setUint16(4, 0, true);                    // disk number
        ev.setUint16(6, 0, true);                    // disk with start of CD
        ev.setUint16(8, files.length, true);         // entries on this disk
        ev.setUint16(10, files.length, true);        // total entries
        ev.setUint32(12, cdSize, true);              // CD size
        ev.setUint32(16, offset, true);              // CD offset
        ev.setUint16(20, 0, true);                   // comment length

        const allParts: Uint8Array[] = [];
        for (let i: number = 0; i < files.length; i++) {
            allParts.push(localHeaders[i]);
            allParts.push(fileData[i]);
        }
        for (const cd of centralDirs) { allParts.push(cd); }
        allParts.push(eocd);

        let totalSize: number = 0;
        for (const p of allParts) { totalSize += p.length; }
        const result: Uint8Array = new Uint8Array(totalSize);
        let pos: number = 0;
        for (const p of allParts) { result.set(p, pos); pos += p.length; }
        return result;
    };

    /** CRC-32 using reflected polynomial 0xEDB88320. */
    private static _Crc32 = (data: Uint8Array): number => {
        const table: Uint32Array = new Uint32Array(256);
        for (let n: number = 0; n < 256; n++) {
            let c: number = n;
            for (let k: number = 0; k < 8; k++) {
                c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
            }
            table[n] = c;
        }
        let crc: number = 0xFFFFFFFF;
        for (let i: number = 0; i < data.length; i++) {
            crc = table[(crc ^ data[i]) & 0xFF] ^ (crc >>> 8);
        }
        return (crc ^ 0xFFFFFFFF) >>> 0;
    };

    // #endregion

    // #region String helpers

    /**
     * Normalises a version string to 4-part Dataverse format (Major.Minor.Build.Revision).
     * "2.1" → "2.1.0.0", "2.1.3" → "2.1.3.0", "" / undefined → "1.0.0.0".
     * Non-numeric segments are replaced with 0.
     */
    private static _NormalizeVersion = (version?: string): string => {
        if (!version || !version.trim()) { return "1.0.0.0"; }
        const parts: string[] = version.trim().split(".");
        const nums: number[] = [0, 0, 0, 0].map((_, i): number => {
            const n: number = parseInt(parts[i] ?? "0", 10);
            return isNaN(n) ? 0 : n;
        });
        return nums.join(".");
    };

    /** Sanitises a base name into a valid Dataverse solution unique name, appending an 8-char GUID suffix. */
    private static _MakeSolutionUniqueName = (baseName: string, flowGuidNoDashes: string): string => {
        let name: string = baseName.replace(/[^a-zA-Z0-9_]/g, "_");
        if (/^\d/.test(name)) { name = "Sol_" + name; }
        return name.substring(0, 91) + "_" + flowGuidNoDashes.substring(0, 8);
    };

    /** Generates a random RFC 4122 v4 GUID. */
    private static _GenerateGuid = (): string => {
        const rn = (n: number): string => Array.from(
            { length: n },
            (): string => Math.floor(Math.random() * 16).toString(16)
        ).join("");
        return `${rn(8)}-${rn(4)}-4${rn(3)}-${["8", "9", "a", "b"][Math.floor(Math.random() * 4)]}${rn(3)}-${rn(12)}`;
    };

    /**
     * Converts a connector API name to a Dataverse-valid connection reference logical name.
     * Dataverse requires a publisher prefix; we use "new_" (the default publisher prefix).
     * Example: "shared_sharepointonline" → "new_sharedsharepointonline"
     */
    private static _MakeConnRefLogicalName = (apiName: string): string =>
        "new_" + apiName.replace(/[^a-zA-Z0-9]/g, "");

    /** Escapes XML text content: &, <, > */
    private static _EscapeXml = (s: string): string => {
        return s
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;");
    };

    /** Escapes XML attribute values: &, <, >, " */
    private static _EscapeXmlAttr = (s: string): string => {
        return s
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;");
    };

    // #endregion
}
